import React, { useState } from "react";
import { Button, Input, cn } from "./ui";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

const TimeInput = (props) => <Input type="time" className="w-auto min-w-[112px] px-2 py-1.5 text-[13px]" {...props} />;

const AvailabilityScheduler = ({ value, onChange }) => {
  const weekly = value.weekly || [];
  const exceptions = value.exceptions || [];
  const byDay = {};
  DAYS.forEach((day) => { byDay[day] = { day, enabled: false, slots: [] }; });
  weekly.forEach((entry) => { if (byDay[entry.day]) byDay[entry.day] = { ...byDay[entry.day], ...entry }; });

  const emit = (patch) => onChange({ ...value, ...patch });
  const emitWeekly = (next) => emit({ weekly: DAYS.map((day) => next[day]) });

  const toggleDay = (day) => {
    const current = byDay[day];
    const next = { ...byDay };
    next[day] = current.enabled
      ? { ...current, enabled: false }
      : { ...current, enabled: true, slots: current.slots.length ? current.slots : [{ start: "09:00", end: "17:00" }] };
    emitWeekly(next);
  };
  const addSlot = (day) => {
    const next = { ...byDay };
    next[day] = { ...byDay[day], slots: [...byDay[day].slots, { start: "09:00", end: "17:00" }] };
    emitWeekly(next);
  };
  const updateSlot = (day, index, field, val) => {
    const next = { ...byDay };
    next[day] = { ...byDay[day], slots: byDay[day].slots.map((slot, i) => (i === index ? { ...slot, [field]: val } : slot)) };
    emitWeekly(next);
  };
  const removeSlot = (day, index) => {
    const next = { ...byDay };
    next[day] = { ...byDay[day], slots: byDay[day].slots.filter((_, i) => i !== index) };
    emitWeekly(next);
  };
  const copyMonToWeekdays = () => {
    const { slots, enabled } = byDay.Mon;
    const next = { ...byDay };
    ["Tue", "Wed", "Thu", "Fri"].forEach((day) => {
      next[day] = { day, enabled, slots: slots.map((slot) => ({ ...slot })) };
    });
    emitWeekly(next);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [editing, setEditing] = useState(null);
  const exMap = {};
  exceptions.forEach((exception) => { exMap[exception.date] = exception; });
  const firstDow = (new Date(view.year, view.month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();

  const prevMonth = () => setView((current) => current.month === 0 ? { year: current.year - 1, month: 11 } : { year: current.year, month: current.month - 1 });
  const nextMonth = () => setView((current) => current.month === 11 ? { year: current.year + 1, month: 0 } : { year: current.year, month: current.month + 1 });
  const upsertException = (dateStr, exception) => {
    const others = exceptions.filter((item) => item.date !== dateStr);
    emit({ exceptions: exception ? [...others, exception] : others });
  };
  const openDay = (day) => {
    const cellDate = new Date(view.year, view.month, day);
    if (cellDate < today) return;
    setEditing(ymd(view.year, view.month, day));
  };
  const editingEx = editing ? exMap[editing] || { date: editing, available: null, slots: [] } : null;
  const setEditingEx = (patch) => upsertException(editing, { ...editingEx, ...patch });

  return (
    <div className="font-dm">
      <h3 className="m-0 font-nunito text-[15px] font-extrabold text-giggs-ink">Weekly hours</h3>
      <p className="mb-4 mt-1 text-[13px] text-giggs-muted">Set the hours you're normally available each week.</p>

      <div className="flex flex-col gap-2">
        {DAYS.map((day) => {
          const dayValue = byDay[day];
          return (
            <div key={day} className="rounded-[10px] border border-slate-200 px-3.5 py-3">
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => toggleDay(day)}
                  className={cn('relative mt-0.5 h-[22px] w-10 shrink-0   rounded-full border-0 transition-colors', dayValue.enabled ? 'bg-giggs-green' : 'bg-[#CBD5D0]')}
                  aria-pressed={dayValue.enabled}
                >
                  <span className={cn('absolute top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow-sm transition-[left] duration-150', dayValue.enabled ? 'left-5' : 'left-0.5')} />
                </button>
                <span className="mt-0.5 w-10 shrink-0 font-semibold text-giggs-ink">{day}</span>
                {!dayValue.enabled ? (
                  <span className="mt-0.5 text-[13px] text-giggs-muted">Unavailable</span>
                ) : (
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-2">
                      {dayValue.slots.map((slot, index) => (
                        <div key={`${day}-${index}`} className="flex flex-wrap items-center gap-2">
                          <TimeInput value={slot.start} onChange={(e) => updateSlot(day, index, "start", e.target.value)} />
                          <span className="text-giggs-muted">to</span>
                          <TimeInput value={slot.end} onChange={(e) => updateSlot(day, index, "end", e.target.value)} />
                          <Button size="sm" variant="ghost" className="min-h-[32px] px-2 text-red-500 hover:bg-red-50 hover:text-red-600" onClick={() => removeSlot(day, index)}>×</Button>
                        </div>
                      ))}
                    </div>
                    <Button size="sm" variant="link" className="mt-2" onClick={() => addSlot(day)}>+ Add hours</Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Button size="sm" variant="link" className="mt-2" onClick={copyMonToWeekdays}>Copy Monday's hours to Tue–Fri</Button>

      <div className="mt-6 border-t border-slate-100 pt-4">
        <h3 className="m-0 font-nunito text-[15px] font-extrabold text-giggs-ink">Date overrides</h3>
        <p className="mb-4 mt-1 text-[13px] text-giggs-muted">Block a day off or set special hours for a specific date.</p>

        <div className="mb-2.5 flex items-center justify-between">
          <Button size="icon" variant="outline" className="h-8 w-8 min-h-0 rounded-lg text-base" onClick={prevMonth}>‹</Button>
          <strong className="font-nunito text-sm text-giggs-ink">{MONTHS[view.month]} {view.year}</strong>
          <Button size="icon" variant="outline" className="h-8 w-8 min-h-0 rounded-lg text-base" onClick={nextMonth}>›</Button>
        </div>

        <div className="grid grid-cols-7 gap-1">
          {DAYS.map((day) => <div key={day} className="py-1 text-center text-[11px] font-semibold text-giggs-muted">{day}</div>)}
          {Array.from({ length: firstDow }).map((_, index) => <div key={`blank-${index}`} />)}
          {Array.from({ length: daysInMonth }).map((_, index) => {
            const dayNum = index + 1;
            const dateStr = ymd(view.year, view.month, dayNum);
            const cellDate = new Date(view.year, view.month, dayNum);
            const isPast = cellDate < today;
            const exception = exMap[dateStr];
            const isEditing = editing === dateStr;
            return (
              <button
                key={dateStr}
                type="button"
                disabled={isPast}
                onClick={() => openDay(dayNum)}
                className={cn(
                  'aspect-square   rounded-lg border text-[13px] font-semibold transition',
                  isPast && 'cursor-default border-slate-100 bg-[#F5F7F6] text-[#C5D2CB]',
                  !isPast && !exception && 'border-slate-200 bg-white text-giggs-ink hover:border-giggs-green',
                  !isPast && exception?.available === false && 'border-red-200 bg-red-50 text-red-500',
                  !isPast && exception?.available === true && 'border-amber-200 bg-amber-50 text-amber-700',
                  isEditing && '!border-2 !border-giggs-green'
                )}
              >
                {dayNum}
              </button>
            );
          })}
        </div>

        {editing && (
          <div className="mt-3.5 rounded-[10px] border border-slate-200 p-4">
            <div className="mb-3 flex items-center justify-between">
              <strong className="text-giggs-ink">{editing}</strong>
              <Button size="sm" variant="ghost" className="min-h-[30px] px-2 text-giggs-muted" onClick={() => setEditing(null)}>×</Button>
            </div>
            <div className="mb-3 grid gap-2 sm:grid-cols-2">
              <Button variant={editingEx.available === false ? 'danger' : 'outline'} onClick={() => setEditingEx({ available: false, slots: [] })}>Block day off</Button>
              <Button variant={editingEx.available === true ? 'primary' : 'outline'} onClick={() => setEditingEx({ available: true, slots: editingEx.slots.length ? editingEx.slots : [{ start: "09:00", end: "17:00" }] })}>Custom hours</Button>
            </div>

            {editingEx.available && (
              <div className="flex flex-col gap-2">
                {editingEx.slots.map((slot, index) => (
                  <div key={`${editing}-${index}`} className="flex flex-wrap items-center gap-2">
                    <TimeInput value={slot.start} onChange={(e) => setEditingEx({ slots: editingEx.slots.map((item, i) => i === index ? { ...item, start: e.target.value } : item) })} />
                    <span className="text-giggs-muted">to</span>
                    <TimeInput value={slot.end} onChange={(e) => setEditingEx({ slots: editingEx.slots.map((item, i) => i === index ? { ...item, end: e.target.value } : item) })} />
                    <Button size="sm" variant="ghost" className="min-h-[32px] px-2 text-red-500 hover:bg-red-50" onClick={() => setEditingEx({ slots: editingEx.slots.filter((_, i) => i !== index) })}>×</Button>
                  </div>
                ))}
                <Button size="sm" variant="link" className="self-start" onClick={() => setEditingEx({ slots: [...editingEx.slots, { start: "09:00", end: "17:00" }] })}>+ Add hours</Button>
              </div>
            )}

            {exMap[editing] && <Button size="sm" variant="link" className="mt-3 text-red-500 hover:text-red-600" onClick={() => { upsertException(editing, null); setEditing(null); }}>Remove override</Button>}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailabilityScheduler;
